import { useCallback, useMemo } from "react"

import type { StoreField } from "~/utils/allocationStores"
import { showErrorToast } from "~/utils/toaster"
import type { SaveCallbacks } from "../../DraftCommandbar"
import { useReplenishmentRules } from "../../useAllocationData"
import {
  stageStoreEdit,
  stagedStoreValues,
  storeDraftChanges,
  storeDraftPatches,
  type StoreValues,
} from "../drafts/storeDraftState"
import { noteRunAfterCurrent, useSetupDrafts } from "../setupDrafts"
import { useStoreSetup } from "./useStoreSetup"

/** Staged store metadata from Settings › Stores, shared by the grid and the inspector. */
export function useStoreSetupDraft() {
  const { storeDraft, setStoreDraft, saveStores } = useSetupDrafts()
  const { stores, isLoading } = useStoreSetup()
  const { rules } = useReplenishmentRules()
  const ruleNames = useMemo(() => new Map(rules.map((rule) => [rule.id, rule.name])), [rules])
  const ruleName = useCallback((ruleId: string) => ruleNames.get(ruleId) ?? ruleId, [ruleNames])
  const editsOf = useCallback((storeId: string) => storeDraft[storeId], [storeDraft])
  const stagedValues = useCallback(
    (store: { id: string; values: StoreValues }) =>
      stagedStoreValues(store.values, storeDraft[store.id]),
    [storeDraft],
  )
  const changes = useMemo(() => storeDraftChanges(storeDraft, stores), [storeDraft, stores])
  const saving = saveStores.isPending
  const stage = useCallback(
    <F extends StoreField>(
      store: { id: string; values: StoreValues },
      field: F,
      value: StoreValues[F],
    ) => {
      if (saving) return
      setStoreDraft((prev) => stageStoreEdit(prev, store.id, store.values, field, value))
    },
    [saving, setStoreDraft],
  )

  return {
    stores,
    isLoading,
    rules,
    ruleName,
    editsOf,
    stagedValues,
    stage,
    changes,
    saving,
    discard: () => setStoreDraft({}),
    save: (callbacks?: SaveCallbacks) =>
      saveStores.mutate(
        { stores: storeDraftPatches(storeDraft) },
        {
          onSuccess: (result) => {
            setStoreDraft({})
            noteRunAfterCurrent(result.run)
            callbacks?.onSaved?.()
          },
          onError: (error) => {
            showErrorToast(error)
            callbacks?.onFailed?.()
          },
        },
      ),
  }
}

export type StoreSetupDraft = ReturnType<typeof useStoreSetupDraft>
